/**
 * Persistent left nav for VIVA HUB (desktop fixed + mobile drawer).
 */

import React, { useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useSchedulerAuth } from '../../contexts/SchedulerAuthContext'
import { getAccessibleApps, getAppPath } from '../../constants/hub-apps'
import { HUB_SUPPORT_APP } from '../../constants/hub-support-app'
import VersionBadge from '../VersionBadge'
import HubAppIcon from './HubAppIcon'
import HubBrandLogo from './HubBrandLogo'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'

function navItemClass({ isActive }) {
  return [
    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-semibold transition-colors',
    isActive
      ? 'bg-green-50 text-green-800'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  ].join(' ')
}

function isAppActive(pathname, appPath) {
  if (!appPath) return false
  if (appPath === '/hub') return pathname === '/hub' || pathname === '/hub/'
  if (appPath.startsWith('/scheduler')) {
    return pathname.startsWith('/scheduler')
  }
  return pathname === appPath || pathname.startsWith(`${appPath}/`)
}

export default function HubSidebar({ open, onClose }) {
  const { user } = useSchedulerAuth()
  const { pathname } = useLocation()
  const accessibleApps = getAccessibleApps(user)
  const drawerRef = useRef(null)
  const previouslyFocused = useRef(null)

  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return undefined
    previouslyFocused.current = document.activeElement
    const panel = drawerRef.current
    const focusables = panel?.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    focusables?.[0]?.focus?.()

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.()
        return
      }
      if (e.key !== 'Tab' || !panel || !focusables?.length) return
      const list = Array.from(focusables)
      const first = list[0]
      const last = list[list.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      previouslyFocused.current?.focus?.()
    }
  }, [open, onClose])

  const sidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2 border-b border-border px-4 sm:h-16">
        <Link to="/hub" onClick={onClose} className="flex min-w-0 items-center gap-2">
          <HubBrandLogo alt="" className="h-8 w-8 shrink-0" />
          <span className="truncate text-xl font-bold text-foreground">VIVA HUB</span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="HUB apps">
        <p className="mb-2 px-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Apps
        </p>
        <ul className="space-y-0.5">
          <li>
            <NavLink to="/hub" end className={navItemClass} onClick={onClose}>
              <HubAppIcon appId="home" className="h-5 w-5 shrink-0" />
              <span>Home</span>
            </NavLink>
          </li>
          {accessibleApps.map((app) => {
            const path = getAppPath(app, user)
            const active = isAppActive(pathname, path)
            return (
              <li key={app.id}>
                <Link
                  to={path}
                  onClick={onClose}
                  className={navItemClass({ isActive: active })}
                  aria-current={active ? 'page' : undefined}
                >
                  <HubAppIcon appId={app.id} className="h-5 w-5 shrink-0" />
                  <span className="truncate">{app.name}</span>
                </Link>
              </li>
            )
          })}
        </ul>

        <p className="mb-2 mt-6 px-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Help
        </p>
        <ul className="space-y-0.5">
          <li>
            <Link
              to={HUB_SUPPORT_APP.path}
              onClick={onClose}
              className={navItemClass({
                isActive: isAppActive(pathname, HUB_SUPPORT_APP.path),
              })}
              aria-current={
                isAppActive(pathname, HUB_SUPPORT_APP.path) ? 'page' : undefined
              }
            >
              <HubAppIcon appId={HUB_SUPPORT_APP.id} className="h-5 w-5 shrink-0" />
              <span className="truncate">{HUB_SUPPORT_APP.name}</span>
            </Link>
          </li>
        </ul>
      </nav>

      <div className="border-t border-border px-4 py-3">
        {user?.name || user?.email ? (
          <p
            className="truncate text-base font-semibold text-foreground"
            title={user.name || user.email}
          >
            {user.name || user.email}
          </p>
        ) : null}
        {user?.email && user?.name ? (
          <p className="truncate text-sm text-muted-foreground" title={user.email}>
            {user.email}
          </p>
        ) : null}
        <VersionBadge className="mt-2 text-xs text-muted-foreground tabular-nums" />
      </div>
    </div>
  )

  return (
    <>
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden w-72 border-r border-border bg-card lg:block"
        aria-label="HUB navigation"
      >
        {sidebarInner}
      </aside>

      <div
        className={[
          'fixed inset-0 z-[60] lg:hidden',
          open ? 'pointer-events-auto' : 'pointer-events-none',
        ].join(' ')}
        aria-hidden={!open}
      >
        <button
          type="button"
          className={[
            'absolute inset-0 bg-black/40 transition-opacity',
            open ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
          aria-label="Close navigation"
          tabIndex={open ? 0 : -1}
          onClick={onClose}
        />
        <aside
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label="HUB navigation"
          className={[
            'absolute inset-y-0 left-0 w-[min(100vw-3rem,20rem)] border-r border-border bg-card shadow-xl transition-transform duration-200 ease-out',
            open ? 'translate-x-0' : '-translate-x-full',
          ].join(' ')}
        >
          {sidebarInner}
        </aside>
      </div>
    </>
  )
}
